import { useEffect, useState } from 'react';
import type { CatalogEntry } from '../../../api/admin';
import type { ConnectionRow, DryRun, WorkflowGraph } from '../../../api/forms';
import { testWorkflowApi } from '../../../api/forms';
import { Modal } from '../../../components/fs';
import { branchExits, topoOrder } from './graph';

/**
 * "What would this do?" — the whole flow, against a sample, sending nothing.
 *
 * The node editor already previews one step. This is the question the canvas
 * makes askable and the list never could: given this submission, which way does
 * it go, and what does each step along that path send?
 *
 * It matters more here than it did on the Connections tab. A workflow is live
 * the moment it is clean — there is no Publish to hide behind — so without this
 * the only way to find out what a change does is to let a real submission find
 * out for you.
 *
 * The CURRENT drawing is sent, saved or not, so a change can be tried before it
 * is trusted.
 */
export default function TryItModal({
  formId, graph, connections, catalog, onClose,
}: {
  formId: number;
  graph: WorkflowGraph;
  connections: ConnectionRow[];
  catalog: CatalogEntry[];
  onClose: () => void;
}) {
  const [sample, setSample] = useState<'latest' | 'example'>('latest');
  const [run, setRun] = useState<DryRun | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [open, setOpen] = useState<string | null>(null);

  const connById = new Map(connections.map((c) => [c.id, c]));
  const nodeById = new Map(graph.nodes.map((n) => [n.id, n]));
  const specOf = (type?: string) => catalog.find((c) => c.type === type);

  useEffect(() => {
    let live = true;
    setBusy(true); setError(''); setRun(null);
    testWorkflowApi(formId, graph, sample)
      .then((r) => { if (live) setRun(r.run); })
      .catch((e) => { if (live) setError(e.message); })
      .finally(() => { if (live) setBusy(false); });
    return () => { live = false; };
  }, [formId, graph, sample]);

  /** The path, in run order, with the branches it passed through named. */
  const walked = run
    ? topoOrder(graph).filter((n) => run.taken.includes(n.id))
    : [];

  const TONE: Record<string, string> = {
    'would-send': 'ok', skipped: 'neutral', off: 'neutral', broken: 'danger',
  };
  const WORD: Record<string, string> = {
    'would-send': 'would send', skipped: 'skipped', off: 'off', broken: 'cannot run',
  };

  return (
    <Modal
      wide
      title="Try this workflow"
      aside="nothing is sent, and nothing is saved"
      onClose={onClose}
      footer={
        <>
          <button className="fs-btn neutral" onClick={onClose}>Close</button>
          <span style={{ flex: 1 }} />
          <span>{run ? `Against ${run.sampleSource}` : ''}</span>
        </>
      }
    >
      <div className="wf-try-sample" style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        <button
          className={`fs-btn sm ${sample === 'latest' ? 'primary' : 'neutral'}`}
          onClick={() => setSample('latest')}
        >The latest submission</button>
        <button
          className={`fs-btn sm ${sample === 'example' ? 'primary' : 'neutral'}`}
          onClick={() => setSample('example')}
        >Example answers</button>
      </div>

      {busy && <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>Working out what would happen…</p>}
      {error && <div className="ad-err">{error}</div>}

      {run && !busy && (
        <>
          <ol className="wf-try">
            {walked.map((node) => {
              const step = run.steps.find((s) => s.nodeId === node.id);
              const conn = node.connectionId ? connById.get(node.connectionId) : undefined;
              const isBranch = node.kind === 'branch';
              const exit = isBranch ? branchExits(node).find((x) => run.ports.includes(`${node.id}\n${x.port}`)) : undefined;
              const side = exit?.port === 'yes' ? 0 : 1;
              // A further path is named by its label, or by its place.
              const label = exit && exit.port !== 'yes' && exit.port !== 'no' ? exit.label || exit.fallback : exit?.label;
              if (node.kind === 'start') return null;
              return (
                <li key={node.id} className={step?.outcome === 'broken' ? 'bad' : ''}>
                  <span className="what">
                    <b>
                      {isBranch ? `? ${String(node.config?.name || 'Which way?')}`
                        : node.kind === 'end' ? `⚑ ${node.config?.mode === 'continue'
                          ? 'Into the follow-up form'
                          : String(node.config?.title || 'Thank you')}`
                          : node.kind === 'merge' ? '↳ The paths rejoin'
                            : `${specOf(conn?.type)?.icon || '•'} ${conn?.name || node.kind}`}
                    </b>
                    {isBranch && <span className="why"> takes “{label || (side ? 'the other path' : 'the first path')}”</span>}
                    {step?.note && <span className="why"> — {step.note}</span>}
                  </span>
                  {step && <span className={`fs-pill ${TONE[step.outcome]}`}>{WORD[step.outcome]}</span>}
                  {step?.preview && (
                    <button className="peek" onClick={() => setOpen(open === node.id ? null : node.id)}>
                      {open === node.id ? 'hide' : 'what it sends'}
                    </button>
                  )}
                  {open === node.id && step?.preview && (
                    <pre className="wf-try-preview wf-try-json">{JSON.stringify(step.preview, null, 2)}</pre>
                  )}
                </li>
              );
            })}
          </ol>

          <div className="fs-consequence" style={{ marginTop: 14 }}>
            {run.endedAt && nodeById.get(run.endedAt)
              ? <>This submission would finish at <b>{
                  nodeById.get(run.endedAt)!.config?.mode === 'continue'
                    ? 'the follow-up form'
                    : String(nodeById.get(run.endedAt)!.config?.title || 'its ending')
                }</b>.</>
              : <>This path reaches no ending, so it falls back to the form’s own After-submitting setting.</>}
          </div>
        </>
      )}
    </Modal>
  );
}
